import React, { useState, useEffect } from 'react';
import { signOut, onIdTokenChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebaseConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    Button,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Card,
    CardContent,
    IconButton,
    useMediaQuery,
    useTheme,
    Grid,
    Alert,
    Collapse
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { formatTotals, addToTotals } from '../currencyOptions';
import { collectLotKeys, sessionBelongsToLots } from '../sessionScope';
import VendorParkingLots from './VendorParkingLots';
import VendorActiveSessions from './VendorActiveSessions';
import VendorLicensePlateLookup from './VendorLicensePlateLookup';
import VendorEarnings from './VendorEarnings';
import VendorSessionHistory from './VendorSessionHistory';
import Reports from './Reports';
import UserLookup from './UserLookup';

const drawerWidth = 240;
const mobileDrawerWidth = 200;

/** receiptTS is written as a Timestamp by some paths and a string by others. */
function toDate(value: any): Date | null {
    if (!value) return null;
    try {
        if (typeof value.toDate === 'function') return value.toDate();
        if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
        return null;
    }
}

interface VendorDashboardProps {
    // Comes from the vendorId custom claim, read in App. Router state is a
    // fallback for the navigate() calls in Login/Signup — on its own it was
    // lost on a browser refresh, which bounced partners back to /login.
    vendorId?: string;
}

export default function VendorDashboard({ vendorId: vendorIdFromClaims }: VendorDashboardProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const vendorId = vendorIdFromClaims || location.state?.vendorId;
    const [selectedPage, setSelectedPage] = useState('overview');
    const [mobileOpen, setMobileOpen] = useState(false);
    const [reportsOpen, setReportsOpen] = useState(false);

    // Overview metrics. Revenue is bucketed per currency rather than summed
    // into one number — lots are priced multi-currency, so a single total
    // with a hardcoded '$' would be wrong for anyone not pricing in USD.
    const [todayRevenue, setTodayRevenue] = useState<Record<string, number>>({});
    const [activeSessions, setActiveSessions] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(true);

    // Email verification UX. Subscribed to onIdTokenChanged so the banner
    // disappears as soon as the partner refreshes their token after
    // verifying (we force-refresh on the "I've verified" button).
    const [emailVerified, setEmailVerified] = useState<boolean>(
        auth.currentUser?.emailVerified ?? true
    );
    const [resendState, setResendState] = useState<'idle' | 'sent' | 'error'>('idle');
    useEffect(() => {
        const unsub = onIdTokenChanged(auth, (u) => {
            setEmailVerified(u?.emailVerified ?? true);
        });
        return unsub;
    }, []);

    const handleResendVerification = async () => {
        if (!auth.currentUser) return;
        try {
            const sendVerification = httpsCallable(
                getFunctions(),
                'sendVendorVerificationEmail'
            );
            await sendVerification({ continueUrl: `${window.location.origin}/login` });
            setResendState('sent');
        } catch (err) {
            console.warn('Resend verification failed:', err);
            setResendState('error');
        }
    };

    const handleVerifiedRefresh = async () => {
        if (!auth.currentUser) return;
        await auth.currentUser.reload();
        // Token refresh triggers onIdTokenChanged above, which updates the
        // banner. Force a token refresh in case the verified flag isn't
        // reflected in cached claims yet.
        await auth.currentUser.getIdToken(true);
        setEmailVerified(auth.currentUser.emailVerified);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    useEffect(() => {
        if (!vendorId) {
            console.error('No vendor ID provided');
            navigate('/login');
        } else {
            fetchOverviewData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId, navigate]);

    const fetchOverviewData = async () => {
        setLoading(true);
        try {
            // Scope by lot document id, not lot name — see sessionScope.ts.
            const lotsSnapshot = await getDocs(query(
                collection(db, 'parkinglots'),
                where('vendorId', '==', vendorId)
            ));
            const { lotIds, lotNames } = collectLotKeys(lotsSnapshot.docs);

            if (lotIds.size === 0) {
                setActiveSessions(0);
                setTodayRevenue({});
                setTotalRevenue({});
                setLoading(false);
                return;
            }

            const sessionsSnapshot = await getDocs(query(
                collection(db, 'parkingsessions'),
                where('sessionActive', '==', true)
            ));
            setActiveSessions(
                sessionsSnapshot.docs.filter((d) =>
                    sessionBelongsToLots(d.data() as any, lotIds, lotNames)
                ).length
            );

            // Payments carry vendorId, so ask Firestore for ours instead of
            // reading the whole collection and matching on lot name. This is
            // the same source the Earnings page uses, so the two agree.
            const paymentsSnapshot = await getDocs(query(
                collection(db, 'payments'),
                where('vendorId', '==', vendorId)
            ));

            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);

            const today: Record<string, number> = {};
            const total: Record<string, number> = {};

            paymentsSnapshot.docs.forEach((d) => {
                const payment = d.data() as any;
                addToTotals(total, payment.amount, payment.currency);

                const paidAt = toDate(payment.receiptTS);
                if (paidAt && paidAt.getTime() >= startOfToday.getTime()) {
                    addToTotals(today, payment.amount, payment.currency);
                }
            });

            setTodayRevenue(today);
            setTotalRevenue(total);
        } catch (error) {
            console.error('Error fetching overview data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMenuClick = (page: string) => {
        setSelectedPage(page);
        if (isMobile) {
            setMobileOpen(false);
        }
        if (page === 'overview') {
            fetchOverviewData();
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        navigate('/login');
    };

    const drawerContent = (
        <>
            <Toolbar />
            <List>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'overview'}
                        onClick={() => handleMenuClick('overview')}
                    >
                        <ListItemText primary="Overview" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'lots'}
                        onClick={() => handleMenuClick('lots')}
                    >
                        <ListItemText primary="My Parking Lots" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'sessions'}
                        onClick={() => handleMenuClick('sessions')}
                    >
                        <ListItemText primary="Active Sessions" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'history'}
                        onClick={() => handleMenuClick('history')}
                    >
                        <ListItemText primary="Session History" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'lookup'}
                        onClick={() => handleMenuClick('lookup')}
                    >
                        <ListItemText primary="License Plate Lookup" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'earnings'}
                        onClick={() => handleMenuClick('earnings')}
                    >
                        <ListItemText primary="Earnings" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => setReportsOpen(!reportsOpen)}>
                        <ListItemText primary="Reports" />
                        {reportsOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>
                <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <ListItemButton
                            sx={{ pl: 4 }}
                            selected={selectedPage === 'rpt-revenue'}
                            onClick={() => handleMenuClick('rpt-revenue')}
                        >
                            <ListItemText primary="Revenue" />
                        </ListItemButton>
                        <ListItemButton
                            sx={{ pl: 4 }}
                            selected={selectedPage === 'rpt-sessions'}
                            onClick={() => handleMenuClick('rpt-sessions')}
                        >
                            <ListItemText primary="Sessions" />
                        </ListItemButton>
                        <ListItemButton
                            sx={{ pl: 4 }}
                            selected={selectedPage === 'rpt-cards'}
                            onClick={() => handleMenuClick('rpt-cards')}
                        >
                            <ListItemText primary="Card Transactions" />
                        </ListItemButton>
                        <ListItemButton
                            sx={{ pl: 4 }}
                            selected={selectedPage === 'rpt-users'}
                            onClick={() => handleMenuClick('rpt-users')}
                        >
                            <ListItemText primary="User Lookup" />
                        </ListItemButton>
                    </List>
                </Collapse>
            </List>
        </>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` }
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{
                            flexGrow: 1,
                            fontSize: { xs: '0.85rem', sm: '1rem', md: '1.25rem' }
                        }}
                    >
                        ARCS Partner Portal{!isMobile && ` - Partner ID: ${vendorId}`}
                    </Typography>
                    <Button color="inherit" onClick={handleLogout}>
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Mobile drawer - only shows when hamburger menu is tapped */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: mobileDrawerWidth },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Desktop drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
                }}
                open
            >
                {drawerContent}
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1.5, sm: 2, md: 3 },
                    width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` }
                }}
            >
                <Toolbar />

                {!emailVerified && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        action={
                            resendState === 'sent' ? (
                                <Button color="inherit" size="small" onClick={handleVerifiedRefresh}>
                                    I've Verified
                                </Button>
                            ) : (
                                <Button color="inherit" size="small" onClick={handleResendVerification}>
                                    Resend Email
                                </Button>
                            )
                        }
                    >
                        {resendState === 'sent'
                            ? "Verification email sent. Check your inbox, then click 'I've Verified'."
                            : resendState === 'error'
                            ? 'Could not resend the verification email. Please try again in a moment.'
                            : 'Please verify your email address to enable lot submission.'}
                    </Alert>
                )}

                {selectedPage === 'overview' && (
                    <Box>
                        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                            Welcome, Partner!
                        </Typography>
                        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: 1 }}>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                <Card>
                                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                            Today's Revenue
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                                            {loading ? '...' : formatTotals(todayRevenue)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                <Card>
                                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                            Active Sessions
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                                            {loading ? '...' : activeSessions}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                <Card>
                                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                            Total Earnings
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                                            {loading ? '...' : formatTotals(totalRevenue)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {selectedPage === 'lots' && (
                    <VendorParkingLots vendorId={vendorId!} canSubmitLots={emailVerified} />
                )}
                {selectedPage === 'sessions' && <VendorActiveSessions vendorId={vendorId!} />}
                {selectedPage === 'lookup' && <VendorLicensePlateLookup vendorId={vendorId!} />}
                {selectedPage === 'earnings' && <VendorEarnings vendorId={vendorId!} />}
                {selectedPage === 'history' && <VendorSessionHistory vendorId={vendorId!} />}
                {selectedPage === 'rpt-revenue' && <Reports key="vrev" fixedType="revenue" lockedVendorId={vendorId!} />}
                {selectedPage === 'rpt-sessions' && <Reports key="vsess" fixedType="sessions" lockedVendorId={vendorId!} />}
                {selectedPage === 'rpt-cards' && <Reports key="vcards" fixedType="cards" lockedVendorId={vendorId!} />}
                {selectedPage === 'rpt-users' && <UserLookup lockedVendorId={vendorId!} />}
            </Box>
        </Box>
    );
}
